import * as React from "react";
import { useEffect, useState, Suspense } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { TextField, Grid, Paper, CircularProgress, Box, Alert, Tooltip, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import { useEnvironmentService as useRegistryEnvironmentService } from "../../../services/registry/EnvironmentService";
import { useEnvironmentService as useBackendEnvironmentService } from "../../../services/backend/EnvironmentService";

interface EnvVariableDefinition {
  description: string;
  defaultValue: string;
  value: string;
}

interface EnvVariableItem {
  key: string;
  description: string;
  defaultValue: string;
  currentOverride: string;
  hasChanged: boolean;
}

let envVariablesCache: EnvVariableItem[] | null = null;
let promise: Promise<EnvVariableItem[]> | null = null;

interface EnvVariablesLoaderProps {
  environment: IEnvironment;
  onEnvVariablesLoaded: (envVariables: EnvVariableItem[]) => void;
  onError: (error: string) => void;
}

// Component that throws a promise while loading environment variables
const EnvVariablesLoader: React.FC<EnvVariablesLoaderProps> = ({ environment, onEnvVariablesLoaded, onError }) => {
  const registryEnvironmentService = useRegistryEnvironmentService();
  const backendEnvironmentService = useBackendEnvironmentService();

  const loadEnvVariables = async (): Promise<EnvVariableItem[]> => {
    // Fetch all environment variables
    const envVarsResponse = await backendEnvironmentService.getEnvVariables();
    const envVarsData: Record<string, EnvVariableDefinition> = envVarsResponse.variables || {};

    // Fetch current overrides from the devenv-variables-overrides feature
    const featuresResponse = await registryEnvironmentService.getAllFeatures(environment.name);
    const currentOverrides = (featuresResponse.features?.["devenv-variables-overrides"] as Record<string, string>) || {};

    const envVariableItems: EnvVariableItem[] = Object.entries(envVarsData).map(([key, definition]) => {
      return {
        key,
        description: definition.description,
        defaultValue: definition.value, // Using 'value' as the default (current system value)
        currentOverride: currentOverrides[key] || "",
        hasChanged: false,
      };
    });

    // Sort alphabetically by key
    envVariableItems.sort((a, b) => a.key.localeCompare(b.key));
    return envVariableItems;
  };

  if (envVariablesCache !== null) {
    return null;
  }

  if (promise) {
    throw promise;
  }
  promise = loadEnvVariables();
  promise
    .then((envVariableItems) => {
      envVariablesCache = envVariableItems;
      onEnvVariablesLoaded(envVariableItems);
    })
    .catch(() => {
      envVariablesCache = [];
      onError("Failed to load environment variables.");
    });
  throw promise;
};

export interface EnvironmentVariablesOverridesPopupProps {
  visible: boolean;
  environment: IEnvironment;
  onDismiss: () => void;
  onSave: (environment: IEnvironment, overrides: Record<string, string>) => Promise<void>;
}

export const EnvironmentVariablesOverridesPopup = ({
  visible,
  environment,
  onDismiss,
  onSave,
}: EnvironmentVariablesOverridesPopupProps) => {
  const [saving, setSaving] = useState(false);
  const [envVariables, setEnvVariables] = useState<EnvVariableItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState("");

  useEffect(() => {
    if (!visible) {
      setEnvVariables([]);
      setError(null);
      setFilterText("");
      // Clear cache and promise when popup is closed
      envVariablesCache = null;
      promise = null;
    }
  }, [visible, environment.name]);

  const handleEnvVariablesLoaded = (loadedEnvVariables: EnvVariableItem[]) => {
    setEnvVariables(loadedEnvVariables);
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleEnvVariableChange = (key: string, newValue: string) => {
    setEnvVariables(
      envVariables.map((envVar) => {
        if (envVar.key === key) {
          const hasChanged = newValue !== (envVar.currentOverride || "");
          return {
            ...envVar,
            currentOverride: newValue,
            hasChanged,
          };
        }
        return envVar;
      }),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Build the overrides object with only non-empty values
      const overrides: Record<string, string> = {};
      envVariables.forEach((envVar) => {
        if (envVar.currentOverride && envVar.currentOverride.trim() !== "") {
          overrides[envVar.key] = envVar.currentOverride;
        }
      });

      await onSave(environment, overrides);
    } catch (error) {
      console.error("Failed to save environment variable overrides:", error);
      setError("Failed to save environment variable overrides. Please try again.");
    } finally {
      setSaving(false);
      onDismiss();
    }
  };

  const getChangedVariablesCount = () => {
    return envVariables.filter((v) => v.hasChanged).length;
  };

  const filteredEnvVariables = envVariables.filter((envVar) => envVar.key.toLowerCase().includes(filterText.toLowerCase()));

  const renderContent = () => {
    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      );
    }

    return (
      <Suspense
        fallback={
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        }
      >
        {visible && (
          <EnvVariablesLoader
            environment={environment}
            onEnvVariablesLoaded={handleEnvVariablesLoaded}
            onError={handleError}
          />
        )}
        <Box sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            label="Filter variables"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Type to filter..."
          />
        </Box>
        <Paper sx={{ p: 2, maxHeight: 500, overflow: "auto" }}>
          <Grid container spacing={2}>
            {filteredEnvVariables.map((envVar) => {
              return (
                <Grid key={envVar.key} size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: envVar.hasChanged ? "action.hover" : "transparent",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", width: 400, flexShrink: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: envVar.hasChanged ? "bold" : "normal",
                          color: envVar.hasChanged ? "primary.main" : "text.primary",
                          fontFamily: "monospace",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {envVar.key}
                      </Typography>
                      <Tooltip title={envVar.description} arrow>
                        <IconButton size="small" sx={{ ml: 0.5 }}>
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ width: 250, flexShrink: 0 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={envVar.defaultValue}
                        disabled
                        label="Default Value"
                        sx={{
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "rgba(0, 0, 0, 0.6)",
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                          },
                        }}
                      />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 250 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={envVar.currentOverride}
                        onChange={(e) => handleEnvVariableChange(envVar.key, e.target.value)}
                        placeholder="(no override)"
                        label="Override Value"
                        sx={{
                          "& .MuiInputBase-input": {
                            fontFamily: "monospace",
                            fontSize: "0.85rem",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
          Showing {filteredEnvVariables.length} of {envVariables.length} variables
        </Typography>
      </Suspense>
    );
  };

  return (
    <Dialog open={visible} onClose={onDismiss} maxWidth="lg" fullWidth>
      <DialogTitle>
        Override Environment Variables - <b>{environment.name}</b>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 400 }}>{renderContent()}</DialogContent>
      <DialogActions>
        {getChangedVariablesCount() > 0 && (
          <Typography variant="body2" color="primary" sx={{ ml: 2 }}>
            {getChangedVariablesCount()} variable(s) will be updated.
          </Typography>
        )}
        <div style={{ flexGrow: 1 }} />
        <Button onClick={onDismiss} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving}>
          {saving ? (
            <CircularProgress size={20} />
          ) : (
            `Save${getChangedVariablesCount() > 0 ? ` (${getChangedVariablesCount()})` : ""}`
          )}
        </Button>
        <div style={{ marginRight: 8 }} />
      </DialogActions>
    </Dialog>
  );
};
